// Phase 44.31.10 — Admin Driver Edit Drawer
// Specifico per ruolo driver: form dati base + veicolo + scadenze + IBAN.
// Riusa PATCH /admin/users/{id} con payload `driver_info` (nested update).
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Save, X, KeyRound, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const VEHICLE_TYPES = [
  { value: "moto",        label: "Moto" },
  { value: "scooter",     label: "Scooter" },
  { value: "auto",        label: "Auto" },
  { value: "auto_grande", label: "Auto grande" },
  { value: "furgone",     label: "Furgone" },
];

const ACCOUNT_STATUSES = [
  { value: "active",    label: "Attivo" },
  { value: "suspended", label: "Sospeso" },
];

// Campi sensibili (richiedono reason)
const SENSITIVE_FIELDS = new Set([
  "email", "phone", "phone_prefix", "account_status",
  "driver_info.vehicle_plate",
  "driver_info.license_number", "driver_info.license_expiry",
  "driver_info.insurance_expiry",
  "driver_info.payout_iban", "driver_info.payout_account_holder",
]);

const initialForm = (d) => {
  const info = d?.driver_info || {};
  return {
    name:          d?.name || "",
    email:         d?.email || "",
    phone_prefix:  d?.phone_prefix || "+41",
    phone:         d?.phone || "",
    account_status: d?.account_status || "active",
    admin_internal_notes: d?.admin_internal_notes || "",
    // driver_info
    vehicle_type:   info.vehicle_type || "",
    vehicle_brand:  info.vehicle_brand || "",
    vehicle_model:  info.vehicle_model || "",
    vehicle_color:  info.vehicle_color || "",
    vehicle_plate:  info.vehicle_plate || "",
    license_number: info.license_number || "",
    license_expiry: info.license_expiry || "",
    insurance_company: info.insurance_company || "",
    insurance_expiry:  info.insurance_expiry || "",
    payout_iban:     info.payout_iban || "",
    payout_account_holder: info.payout_account_holder || "",
    payout_bank_name: info.payout_bank_name || "",
  };
};

export default function AdminDriverEditDrawer({ driver, onClose, onSaved }) {
  const [form, setForm] = useState(() => initialForm(driver));
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);

  useEffect(() => { setForm(initialForm(driver)); setReason(""); }, [driver]);

  const onField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Costruisce payload `changes` per PATCH /admin/users/{id}
  const { changes, touchedSensitive } = useMemo(() => {
    if (!driver) return { changes: {}, touchedSensitive: false };
    const original = initialForm(driver);
    const c = {};
    const sensitiveTouched = new Set();
    // Top-level
    for (const f of ["name", "email", "phone", "phone_prefix", "account_status", "admin_internal_notes"]) {
      if ((form[f] || "") !== (original[f] || "")) {
        c[f] = form[f] || null;
        if (SENSITIVE_FIELDS.has(f)) sensitiveTouched.add(f);
      }
    }
    // driver_info nested
    const subDiff = {};
    const subFields = [
      "vehicle_type", "vehicle_brand", "vehicle_model", "vehicle_color", "vehicle_plate",
      "license_number", "license_expiry",
      "insurance_company", "insurance_expiry",
      "payout_iban", "payout_account_holder", "payout_bank_name",
    ];
    for (const f of subFields) {
      if ((form[f] || "") !== (original[f] || "")) {
        subDiff[f] = form[f] || null;
        if (SENSITIVE_FIELDS.has(`driver_info.${f}`)) sensitiveTouched.add(`driver_info.${f}`);
      }
    }
    if (Object.keys(subDiff).length > 0) c.driver_info = subDiff;
    return { changes: c, touchedSensitive: sensitiveTouched.size > 0 };
  }, [form, driver]);

  const reasonRequired = touchedSensitive;
  const canSave = Object.keys(changes).length > 0 && (!reasonRequired || reason.trim().length >= 3);

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/users/${driver.id}`, {
        changes,
        reason: reason || null,
      });
      toast.success(`Salvate ${data.changed || 0} modifiche`);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const sendPasswordReset = async () => {
    if (!driver) return;
    if (!window.confirm("Inviare email reset password al driver?")) return;
    setSendingReset(true);
    try {
      await api.post(`/admin/users/${driver.id}/send-password-reset`);
      toast.success("Email reset password inviata");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSendingReset(false);
    }
  };

  if (!driver) return null;

  return (
    <div className="fixed inset-0 z-[100] flex" data-testid="admin-driver-edit-drawer">
      <div className="flex-1 bg-black/50" onClick={onClose} />
      <aside className="w-full sm:w-[500px] bg-white shadow-2xl flex flex-col h-full overflow-hidden">
        <header className="flex items-start justify-between gap-3 p-5 border-b border-dapnow-line shrink-0">
          <div className="min-w-0 flex-1">
            <p className="overline text-[10px]">Modifica trasportatore</p>
            <h2 className="font-display text-2xl font-extrabold tracking-tighter">{driver.name || "—"}</h2>
            <p className="text-xs text-dapnow-muted mt-1">Driver · {driver.email}</p>
          </div>
          <button onClick={onClose} aria-label="Chiudi" data-testid="driver-edit-close"
            className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-dapnow-line hover:border-dapnow-ink"><X className="h-4 w-4" /></button>
        </header>

        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          {/* DATI BASE */}
          <Section title="Dati base">
            <Field label="Nome">
              <Input value={form.name} onChange={(v) => onField("name", v)} testid="drv-edit-name" />
            </Field>
            <Field label="Email" sensitive>
              <Input value={form.email} onChange={(v) => onField("email", v)} type="email" testid="drv-edit-email" />
            </Field>
            <div className="grid grid-cols-[100px_1fr] gap-3">
              <Field label="Prefisso" sensitive>
                <Select value={form.phone_prefix} onChange={(v) => onField("phone_prefix", v)} testid="drv-edit-prefix">
                  <option value="+41">+41</option>
                  <option value="+39">+39</option>
                  <option value="+33">+33</option>
                  <option value="+49">+49</option>
                </Select>
              </Field>
              <Field label="Telefono" sensitive>
                <Input value={form.phone} onChange={(v) => onField("phone", v)} testid="drv-edit-phone" />
              </Field>
            </div>
          </Section>

          {/* ACCOUNT */}
          <Section title="Account">
            <Field label="Stato account" sensitive>
              <Select value={form.account_status} onChange={(v) => onField("account_status", v)} testid="drv-edit-status">
                {ACCOUNT_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </Select>
            </Field>
          </Section>

          {/* VEICOLO */}
          <Section title="Veicolo">
            <Field label="Tipo">
              <Select value={form.vehicle_type} onChange={(v) => onField("vehicle_type", v)} testid="drv-edit-vehicle-type">
                <option value="">— Seleziona —</option>
                {VEHICLE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </Select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Marca">
                <Input value={form.vehicle_brand} onChange={(v) => onField("vehicle_brand", v)} testid="drv-edit-brand" />
              </Field>
              <Field label="Modello">
                <Input value={form.vehicle_model} onChange={(v) => onField("vehicle_model", v)} testid="drv-edit-model" />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Colore">
                <Input value={form.vehicle_color} onChange={(v) => onField("vehicle_color", v)} testid="drv-edit-color" />
              </Field>
              <Field label="Targa" sensitive>
                <Input value={form.vehicle_plate} onChange={(v) => onField("vehicle_plate", v?.toUpperCase())} testid="drv-edit-plate" />
              </Field>
            </div>
          </Section>

          {/* PATENTE & ASSICURAZIONE */}
          <Section title="Patente e assicurazione">
            <Field label="Numero patente" sensitive>
              <Input value={form.license_number} onChange={(v) => onField("license_number", v)} testid="drv-edit-license-nr" />
            </Field>
            <Field label="Scadenza patente" sensitive hint="Formato: AAAA-MM-GG">
              <Input value={form.license_expiry} onChange={(v) => onField("license_expiry", v)} placeholder="2027-12-31" testid="drv-edit-license-exp" />
            </Field>
            <Field label="Compagnia assicurativa">
              <Input value={form.insurance_company} onChange={(v) => onField("insurance_company", v)} testid="drv-edit-ins-company" />
            </Field>
            <Field label="Scadenza assicurazione" sensitive hint="Formato: AAAA-MM-GG">
              <Input value={form.insurance_expiry} onChange={(v) => onField("insurance_expiry", v)} placeholder="2027-12-31" testid="drv-edit-ins-exp" />
            </Field>
          </Section>

          {/* PAYOUT */}
          <Section title="Pagamento (IBAN)">
            <Field label="IBAN" sensitive>
              <Input value={form.payout_iban} onChange={(v) => onField("payout_iban", v?.toUpperCase().replace(/\s+/g, ""))} placeholder="CH00 0000 0000 0000 0000 0" testid="drv-edit-iban" />
            </Field>
            <Field label="Intestatario" sensitive>
              <Input value={form.payout_account_holder} onChange={(v) => onField("payout_account_holder", v)} testid="drv-edit-holder" />
            </Field>
            <Field label="Banca">
              <Input value={form.payout_bank_name} onChange={(v) => onField("payout_bank_name", v)} testid="drv-edit-bank" />
            </Field>
          </Section>

          {/* NOTE ADMIN */}
          <Section title="Note interne admin">
            <textarea
              value={form.admin_internal_notes}
              onChange={(e) => onField("admin_internal_notes", e.target.value)}
              rows={3}
              data-testid="drv-edit-notes"
              className="w-full rounded-xl border border-dapnow-line bg-white p-3 text-sm"
              placeholder="Note libere visibili solo agli admin…"
            />
          </Section>

          {/* MOTIVO MODIFICA */}
          {touchedSensitive && (
            <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3 space-y-2">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
                <p className="text-xs text-amber-900 font-semibold">Stai modificando campi sensibili. Inserisci il motivo (min 3 caratteri).</p>
              </div>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                data-testid="drv-edit-reason"
                className="w-full rounded-lg border border-amber-300 bg-white p-2 text-xs"
                placeholder="Es. richiesta del driver, errore di trascrizione…"
              />
            </div>
          )}

          {/* AZIONI SECONDARIE */}
          <div className="pt-4 border-t border-dapnow-line">
            <p className="overline mb-2">Azioni</p>
            <button
              type="button"
              onClick={sendPasswordReset}
              disabled={sendingReset}
              data-testid="drv-edit-pwd-reset"
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-xl border border-dapnow-line bg-white text-xs font-semibold hover:border-dapnow-ink disabled:opacity-50"
            >
              <KeyRound className="h-3.5 w-3.5" />
              {sendingReset ? "Invio…" : "Invia reset password"}
            </button>
          </div>
        </div>

        <footer className="border-t border-dapnow-line p-4 flex items-center gap-3 shrink-0 bg-white">
          <button onClick={onClose} className="h-11 px-4 rounded-xl border border-dapnow-line text-sm font-semibold" data-testid="drv-edit-cancel">
            Annulla
          </button>
          <button
            onClick={save}
            disabled={!canSave || saving}
            data-testid="drv-edit-save"
            className="flex-1 inline-flex items-center justify-center gap-2 h-11 rounded-xl bg-dapnow-blue text-white text-sm font-bold disabled:opacity-50"
          >
            <Save className="h-4 w-4" /> {saving ? "Salvo…" : `Salva ${Object.keys(changes).length || ""}`}
          </button>
        </footer>
      </aside>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="space-y-3">
      <p className="overline border-t border-dapnow-line pt-3">{title}</p>
      {children}
    </section>
  );
}

function Field({ label, sensitive, hint, children }) {
  return (
    <label className="block space-y-1.5">
      <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-dapnow-muted">
        {label} {sensitive && <span className="h-1.5 w-1.5 rounded-full bg-amber-500" title="Sensibile" />}
      </span>
      {children}
      {hint && <span className="text-[10px] text-dapnow-muted">{hint}</span>}
    </label>
  );
}

function Input({ value, onChange, type = "text", placeholder, testid }) {
  return (
    <input
      type={type} value={value || ""} onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder} data-testid={testid}
      className="w-full h-11 rounded-xl border border-dapnow-line bg-white px-3 text-sm focus:border-dapnow-blue focus:outline-none"
    />
  );
}

function Select({ value, onChange, children, testid }) {
  return (
    <select
      value={value || ""} onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="w-full h-11 rounded-xl border border-dapnow-line bg-white px-3 text-sm focus:border-dapnow-blue focus:outline-none"
    >
      {children}
    </select>
  );
}
